import { FormattedMessage, useIntl } from 'react-intl';
import Button from './core/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/core/Dialog';
import * as React from 'react';
import { cn } from '@/helpers/classnames';
import { IconXmark } from '@/icons';
import IconButton from './core/IconButton';

interface DialogWindowProps {
  dialogTrigger?: React.ReactNode;
  title: React.ReactNode;
  content: React.ReactNode;
  /** Rendered in the header, before the close button. */
  headerActions?: React.ReactNode;
  /** Rendered in the footer instead of the default close button. */
  footer?: React.ReactNode;
  useFooter?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  /**
   * Ref to the scrollable content container. Pass this to popovers (e.g. a
   * Dropdown's `containerRef`) so their overlay stays within the dialog bounds.
   */
  contentRef?: React.RefObject<HTMLDivElement>;
}

export const DialogWindow = ({
  dialogTrigger,
  title,
  content,
  headerActions,
  footer,
  open,
  onOpenChange,
  useFooter = false,
  className,
  contentClassName,
  contentRef,
}: DialogWindowProps): React.JSX.Element => {
  const { formatMessage } = useIntl();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {dialogTrigger && <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>}
      <DialogContent
        className={cn(
          'max-h-[90vh] w-full min-w-0 max-w-panel-full overflow-hidden text-text-950',
          className,
        )}
      >
        <DialogHeader className="-m-6 mb-0 flex flex-row items-center justify-between bg-primary-500 px-6 py-4">
          <DialogTitle>{title}</DialogTitle>
          <div className="flex flex-row items-center gap-1">
            {headerActions}
            <DialogClose asChild>
              <IconButton
                variant="ghost"
                icon={<IconXmark />}
                aria-label={formatMessage({
                  id: 'dialog.close_dialog',
                  defaultMessage: 'Close dialog',
                })}
              />
            </DialogClose>
          </div>
        </DialogHeader>
        <div
          ref={contentRef}
          className={cn(
            'h-max max-h-[80vh] w-full min-w-0 overflow-y-auto',
            contentClassName,
          )}
        >
          {content}
        </div>
        {footer && <DialogFooter>{footer}</DialogFooter>}
        {!footer && useFooter && (
          <DialogFooter className="sm:justify-start">
            <DialogClose asChild>
              <Button variant="outline">
                <FormattedMessage id="dialog.close" defaultMessage="Close" />
              </Button>
            </DialogClose>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
